import React, { useEffect, useMemo, useState } from 'react';
import useSittingStore from '../../stores/sittingStore';
import HandoffBadge from '../procedures/HandoffBadge';
import {
  listPaymentsForSitting,
  openProcedureReceiptPdf,
} from '../../services/api/patientPlanApi';

/**
 * VisitContextHeader — Phase 1c team-coordination header.
 *
 * Rendered at the top of VisitActionsPanel on every visit, visible to
 * anyone who can see the visit at all. Shows:
 *
 *   1. Patient banner (name · age · sex · mobile) + queue # + primary
 *      doctor + handoff pill.
 *   2. Collapsible "On this visit" team panel:
 *      - Row 1 is always the primary doctor's consultation (synthesised
 *        from the visit row — not stored as a sitting).
 *      - Rows 2+ are sittings from sittingStore.byVisit[visit_id], each
 *        with procedure name + assignee name + status pill.
 *      - Status rows flip live via the store's existing SITTING_* WS
 *        handlers; the component re-renders on any byVisit change.
 *
 * Every row is listed regardless of share-flag visibility — knowing a
 * procedure EXISTS is not sensitive. Drill-down (clicking into a
 * sitting) still respects visibility through the backend's metadata
 * filter.
 */

const STATUS_PILL = {
  awaiting_payment: { label: 'Awaiting payment', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  payment_cleared:  { label: 'Ready',            cls: 'bg-teal-100 text-teal-900 border-teal-300' },
  scheduled:        { label: 'Scheduled',        cls: 'bg-sky-100 text-sky-900 border-sky-300' },
  in_progress:      { label: 'In progress',      cls: 'bg-blue-100 text-blue-900 border-blue-300' },
  pending_approval: { label: 'Awaiting approval', cls: 'bg-purple-100 text-purple-900 border-purple-300' },
  completed:        { label: 'Completed',        cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  paid:             { label: 'Paid',             cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  cancelled:        { label: 'Cancelled',        cls: 'bg-slate-100 text-slate-600 border-slate-300 line-through' },
};

function StatusPill({ status }) {
  const meta = STATUS_PILL[status] || { label: status || '—', cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 border rounded-full ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function VisitStatusPill({ status }) {
  const s = (status || '').toUpperCase();
  let cls = 'bg-slate-100 text-slate-700 border-slate-300';
  let label = s || '—';
  if (s === 'IN_PROGRESS') { cls = 'bg-blue-100 text-blue-900 border-blue-300'; label = 'In consult'; }
  else if (s === 'QUEUE') { cls = 'bg-amber-100 text-amber-900 border-amber-300'; label = 'In queue'; }
  else if (s === 'COMPLETED') { cls = 'bg-emerald-100 text-emerald-900 border-emerald-300'; label = 'Done'; }
  else if (s === 'CANCELLED' || s === 'SKIPPED') {
    cls = 'bg-slate-100 text-slate-600 border-slate-300 line-through';
    label = s === 'SKIPPED' ? 'Skipped' : 'Cancelled';
  }
  return (
    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 border rounded-full ${cls}`}>
      {label}
    </span>
  );
}

export default function VisitContextHeader({ visit, viewerMemberId, isDark }) {
  // Subscribe to byVisit[visit_id] for live team-panel updates.
  const byVisit = useSittingStore((s) => s.byVisit);
  const fetchForVisit = useSittingStore((s) => s.fetchForVisit);

  useEffect(() => {
    if (visit?.visit_id) fetchForVisit(visit.visit_id);
  }, [visit?.visit_id, fetchForVisit]);

  const sittings = useMemo(
    () => (visit?.visit_id ? (byVisit[visit.visit_id] || []) : []),
    [byVisit, visit?.visit_id],
  );

  const [expanded, setExpanded] = useState(true);

  if (!visit) return null;

  const patientName = visit.patient_name
    || [visit.patient_first_name, visit.patient_last_name].filter(Boolean).join(' ')
    || '—';
  const age = visit.patient_age;
  const gender = (visit.patient_gender || '').toUpperCase();
  const genderShort = gender.startsWith('M') ? 'M' : gender.startsWith('F') ? 'F' : gender ? gender[0] : '';
  const mobile = visit.patient_mobile || visit.patient_phone || '';
  const queue = visit.queue_number;
  const doctorName = visit.doctor_name || '';

  const demographics = [
    age ? `${age}` : null,
    genderShort || null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      style={{
        padding: '0.625rem 0.875rem',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
      }}
    >
      {/* ── Patient banner ─────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            flexWrap: 'wrap',
          }}>
            <span style={{
              fontSize: '0.9375rem', fontWeight: 700,
              color: isDark ? '#f1f5f9' : '#0f172a',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              maxWidth: '20rem',
            }}>
              {patientName}
            </span>
            {demographics && (
              <span style={{
                fontSize: '0.75rem',
                color: isDark ? '#cbd5e1' : '#475569',
              }}>
                {demographics}
              </span>
            )}
            {mobile && (
              <span style={{
                fontSize: '0.6875rem',
                color: isDark ? '#94a3b8' : '#64748b',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {mobile}
              </span>
            )}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginTop: '0.125rem',
            fontSize: '0.6875rem',
            color: isDark ? '#94a3b8' : '#64748b',
          }}>
            {queue ? <span>Queue <b>#{queue}</b></span> : null}
            {doctorName ? <span>· Dr. {doctorName}</span> : null}
            <VisitStatusPill status={visit.status} />
            <HandoffBadge visit={visit} viewerMemberId={viewerMemberId} isDark={isDark} />
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: isDark ? '#cbd5e1' : '#374151',
            // RV-6 — inactive-state rule: solid fill, never transparent.
            background: isDark ? '#1e293b' : '#f8fafc',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)'}`,
            borderRadius: '0.375rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
          title={expanded ? 'Hide team panel' : 'Show team panel'}
        >
          {expanded ? '▲ Team' : '▼ Team'}
        </button>
      </div>

      {/* ── Team panel ─────────────────────────────────────────────── */}
      {expanded && (
        <div
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.625rem',
            background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            borderRadius: '0.5rem',
          }}
        >
          <div style={{
            fontSize: '0.625rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.05em',
            color: isDark ? '#64748b' : '#94a3b8',
            marginBottom: '0.375rem',
          }}>
            On this visit
          </div>

          {/* Row 1 — Consultation (synthesized) */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.25rem 0',
            fontSize: '0.75rem',
            color: isDark ? '#e2e8f0' : '#334155',
          }}>
            <span style={{ minWidth: '1rem' }}>🩺</span>
            <span style={{ fontWeight: 600, flex: 1 }}>
              Consultation
            </span>
            <span style={{
              fontSize: '0.6875rem',
              color: isDark ? '#94a3b8' : '#64748b',
            }}>
              {doctorName ? `Dr. ${doctorName}` : '—'}
              {viewerMemberId && visit.doctor_member_id === viewerMemberId ? ' (you)' : ''}
            </span>
            <VisitStatusPill status={visit.status} />
          </div>

          {/* Rows 2+ — procedure sittings */}
          {sittings.length === 0 && (
            <div style={{
              padding: '0.25rem 0',
              fontSize: '0.75rem', fontStyle: 'italic',
              color: isDark ? '#64748b' : '#94a3b8',
            }}>
              No procedures added yet.
            </div>
          )}
          {sittings.map((s) => {
            const assigneeName = s.assigned_to_name
              || s.assigned_to_member_id
              || '—';
            const isSelfAssigned = viewerMemberId && s.assigned_to_member_id === viewerMemberId;
            return (
              <div
                key={s.sitting_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.25rem 0',
                  fontSize: '0.75rem',
                  color: isDark ? '#e2e8f0' : '#334155',
                  borderTop: `1px dashed ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}`,
                }}
              >
                <span style={{ minWidth: '1rem' }}>💉</span>
                <span style={{
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {s.procedure_name || 'Procedure'}
                </span>
                <span style={{
                  fontSize: '0.6875rem',
                  color: isDark ? '#94a3b8' : '#64748b',
                }}>
                  {s.assigned_to_role ? `${s.assigned_to_role} ` : ''}
                  {assigneeName}
                  {isSelfAssigned ? ' (you)' : ''}
                </span>
                {s.requires_approval && (
                  <span
                    style={{
                      fontSize: '0.5625rem', fontWeight: 700,
                      padding: '0.0625rem 0.375rem', borderRadius: '9999px',
                      background: isDark ? 'rgba(168,85,247,0.15)' : 'rgba(168,85,247,0.1)',
                      color: isDark ? '#c4b5fd' : '#7c3aed',
                      border: '1px solid rgba(168,85,247,0.3)',
                    }}
                    title="Needs approval on complete"
                  >
                    APPROVAL
                  </span>
                )}
                <StatusPill status={s.status} />
                {/* Phase 1c — receipt/reprint action per sitting row.
                    Surfaces when payment has cleared on the sitting.
                    Clicking fetches the latest payment for the sitting
                    and opens its PDF (idempotent — works as reprint). */}
                {s.status !== 'awaiting_payment' && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const r = await listPaymentsForSitting(s.plan_id, s.sitting_id);
                      if (r?.success) {
                        const payments = r.data?.payments || [];
                        if (payments.length > 0) {
                          await openProcedureReceiptPdf(
                            payments[payments.length - 1].payment_id,
                          );
                        }
                      }
                    }}
                    title="View/reprint receipt"
                    style={{
                      fontSize: '0.75rem',
                      padding: '0 0.25rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: isDark ? '#94a3b8' : '#64748b',
                    }}
                  >
                    🖨
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
